import React from 'react'
import { Handle, NodeProps, Position } from '@xyflow/react'
import { SIGNAL_TYPE_COLORS } from '@/constants/colors'
import type { ZoneConnectionPoint } from '@busswerks/shared'
import type { SignalType } from '@/types/devices'

/**
 * A "peer zone satellite" is a read-only stand-in for another zone
 * shown inside the drilldown view of the focused zone. It shows
 * only the peer zone's exposed ports in the direction that interacts
 * with the focused zone, so the user can see cross-zone traffic
 * without leaving drilldown.
 *
 *   side='input'  — the satellite sits to the LEFT of the focused
 *                    zone's input wall. Its handles represent the
 *                    peer's OUTPUT connection points — signal flows
 *                    FROM the peer INTO the focused zone.
 *   side='output' — the satellite sits to the RIGHT of the focused
 *                    zone's output wall. Its handles represent the
 *                    peer's INPUT connection points — signal flows
 *                    FROM the focused zone OUT to the peer.
 *
 * Handle types pair with the focused wall's EXTERNAL handles
 * (id suffix ::ext), which sit on the outside edge of each wall
 * row. The external wall handles are the zone's external-facing
 * ports — not to be confused with the internal handles that
 * attach to devices. Wall handle types under strict mode:
 *
 *   input wall  external = 'target' → peer-out sat = 'source'
 *   output wall external = 'source' → peer-in  sat = 'target'
 *
 * The satellite's root `<div>` carries `data-drop-zone-target={peerZoneId}`
 * so the existing drop-target mechanism in EditorClient.handleNodeDragStop
 * reassigns a dragged device into this peer zone without any new code.
 */
export interface PeerSatelliteData {
  peerZoneId: string
  peerZoneName: string
  peerZoneColor: string
  side: 'input' | 'output'
  connectionPoints: ZoneConnectionPoint[]
  labels?: Record<string, { deviceName: string; portName: string }>
}

export const PEER_OUT_NODE_PREFIX = 'peer-out-'
export const PEER_IN_NODE_PREFIX = 'peer-in-'

export function isPeerSatelliteNodeId(nodeId: string): boolean {
  return (
    nodeId.startsWith(PEER_OUT_NODE_PREFIX) ||
    nodeId.startsWith(PEER_IN_NODE_PREFIX)
  )
}

const PeerZoneSatelliteNode: React.FC<NodeProps> = ({ data }) => {
  const satData = data as unknown as PeerSatelliteData
  const isLeftSide = satData.side === 'input'
  // Drag-pair with external wall handles:
  //   - peer-out sat (left of input wall, peer's outputs feeding
  //     focused zone) = 'source' → pairs with input wall external
  //     'target' handles.
  //   - peer-in sat (right of output wall, peer's inputs from
  //     focused zone) = 'target' → pairs with output wall external
  //     'source' handles.
  const handleType: 'source' | 'target' = isLeftSide ? 'source' : 'target'
  // The satellite faces IN toward the focused zone: a left-side
  // satellite's handles are on its right edge, and vice versa.
  const handlePosition = isLeftSide ? Position.Right : Position.Left

  const color = satData.peerZoneColor || '#94a3b8'
  const directionLabel = isLeftSide ? 'outputs' : 'inputs'

  return (
    <div
      className="relative select-none"
      style={{ width: 240, minHeight: 180 }}
      // Drop-target reassignment: handleNodeDragStop walks up from
      // elementFromPoint looking for [data-drop-zone-target] and
      // reassigns the dragged device to that zone id.
      data-drop-zone-target={satData.peerZoneId}
      title={`Drop a device here to move it to ${satData.peerZoneName}`}
    >
      <div
        className="h-full rounded-xl border-2 px-3 py-3 flex flex-col"
        style={{
          backgroundColor: color + '0d',
          borderColor: color + '80',
          borderStyle: 'solid',
          minHeight: 180,
        }}
      >
        <div
          className="text-[10px] font-bold uppercase tracking-widest text-center"
          style={{ color }}
        >
          {satData.peerZoneName}
        </div>
        <div className="text-[9px] text-gray-500 dark:text-gray-400 text-center mt-0.5 mb-2 uppercase tracking-wider">
          · {directionLabel} ·
        </div>

        {satData.connectionPoints.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-[10px] italic text-gray-500 dark:text-gray-400 text-center px-2">
              No exposed {directionLabel}.
            </p>
          </div>
        ) : (
          <ul className="space-y-1.5 flex-1">
            {satData.connectionPoints.map(cp => {
              const portColor =
                SIGNAL_TYPE_COLORS[cp.signalType as SignalType] ?? '#999'
              const label = satData.labels?.[cp.id]
              const deviceName = label?.deviceName
              const portName = label?.portName ?? cp.name
              return (
                <li
                  key={cp.id}
                  className="relative flex items-center gap-2 bg-white/60 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-md px-2 py-1.5"
                >
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: portColor }}
                  />
                  <div className="min-w-0 flex-1 flex items-baseline gap-1.5">
                    {deviceName && (
                      <span
                        className="text-[9px] text-gray-500 dark:text-gray-400 uppercase tracking-wider truncate flex-shrink-0 max-w-[55%]"
                        title={deviceName}
                      >
                        {deviceName}
                      </span>
                    )}
                    <span
                      className="text-[10px] text-gray-700 dark:text-gray-200 truncate"
                      title={portName}
                    >
                      {portName}
                    </span>
                  </div>
                  <Handle
                    id={cp.id}
                    type={handleType}
                    position={handlePosition}
                    style={{
                      background: portColor,
                      width: 12,
                      height: 12,
                      border: '2px solid #0f172a',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      [isLeftSide ? 'right' : 'left']: -14,
                      [isLeftSide ? 'left' : 'right']: 'auto',
                    }}
                    title={`${cp.name} (${cp.signalType})`}
                  />
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}

export default PeerZoneSatelliteNode
